import { TEMAS } from '@shared/conselheiro.js';

/**
 * Radar dos 10 temas do Conselheiro, com várias séries sobrepostas (um sócio
 * por série, a média, ou partida × marco × final). O radar do diagnóstico
 * avulso é fixo em 5 forças; este é o de temas.
 */
export type SerieDoRadar = { rotulo: string; notas: readonly number[]; cor: string; tracejada?: boolean; preenchida?: boolean };

export const CORES_DO_RADAR = ['#60a5fa', '#f472b6', '#34d399', '#a78bfa', '#fb923c', '#22d3ee'];

const N = TEMAS.length;

function ponto(cx: number, cy: number, r: number, i: number, f: number) {
  const ang = ((Math.PI * 2) / N) * i - Math.PI / 2;
  return { x: cx + Math.cos(ang) * r * f, y: cy + Math.sin(ang) * r * f };
}

export function Radar10({ series, destaque = [], tamanho = 420 }: { series: SerieDoRadar[]; destaque?: number[]; tamanho?: number }) {
  const cx = 250;
  const cy = 250;
  const R = 160;
  return (
    <div>
      <svg viewBox="0 0 500 500" width="100%" style={{ maxWidth: tamanho }} role="img" aria-label="Radar dos 10 temas">
        {[2, 4, 6, 8, 10].map((g) => (
          <polygon
            key={g}
            points={TEMAS.map((_, i) => {
              const p = ponto(cx, cy, R, i, g / 10);
              return `${p.x},${p.y}`;
            }).join(' ')}
            fill="none"
            stroke="var(--border)"
            strokeWidth={1}
          />
        ))}
        {TEMAS.map((_, i) => {
          const p = ponto(cx, cy, R, i, 1);
          return <line key={i} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke="var(--border)" strokeWidth={1} />;
        })}
        {series.map((s) => (
          <polygon
            key={s.rotulo}
            points={s.notas
              .map((n, i) => {
                const p = ponto(cx, cy, R, i, Math.max(0, Math.min(10, n)) / 10);
                return `${p.x},${p.y}`;
              })
              .join(' ')}
            fill={s.preenchida ? s.cor : 'none'}
            fillOpacity={s.preenchida ? 0.12 : 0}
            stroke={s.cor}
            strokeWidth={s.preenchida ? 2.5 : 1.8}
            strokeDasharray={s.tracejada ? '5 4' : undefined}
          />
        ))}
        {TEMAS.map((t, i) => {
          const p = ponto(cx, cy, R + 26, i, 1);
          const anc = Math.abs(p.x - cx) < 8 ? 'middle' : p.x > cx ? 'start' : 'end';
          const div = destaque.includes(i);
          return (
            <text
              key={t}
              x={p.x}
              y={p.y}
              textAnchor={anc}
              dominantBaseline="middle"
              fontSize={13}
              fontWeight={div ? 800 : 600}
              fill={div ? '#f59e0b' : 'var(--text2)'}
            >
              {t}
              {div ? ' ⚠' : ''}
            </text>
          );
        })}
      </svg>
      <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs" style={{ color: 'var(--text2)' }}>
        {series.map((s) => (
          <li key={s.rotulo} className="flex items-center gap-1.5">
            <span
              className="inline-block h-0.5 w-5"
              style={{ background: s.tracejada ? 'transparent' : s.cor, borderTop: s.tracejada ? `2px dashed ${s.cor}` : undefined }}
            />
            {s.rotulo}
          </li>
        ))}
      </ul>
    </div>
  );
}
